import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FiLayers, 
  FiArrowUpRight, 
  FiCheck, 
  FiLayout, 
  FiServer, 
  FiGlobe, 
  FiMail, 
  FiCamera, 
  FiPenTool, 
  FiTrendingUp, 
  FiSettings, 
  FiShield, 
  FiCloud 
} from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const coreServices = [
  {
    title: "Website Development",
    desc: "Custom business websites, e-commerce stores, and web applications built for performance and scale.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873327/C93EF2C4-B210-4C83-8EC5-D5004309DD84_rj8hsn.webp",
    icon: FiLayout,
    list: ["Custom, responsive design", "SEO-optimized architecture", "Seamless CMS integration"]
  },
  {
    title: "Hosting Solutions",
    desc: "Lightning-fast cloud hosting, managed servers, and seamless migrations with 99.99% uptime.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873393/92125A1D-0047-4020-84CA-67D0C481CF4E_yguuua.webp",
    icon: FiServer,
    list: ["99.99% uptime guarantee", "Automated daily backups", "Free SSL certificates"]
  },
  {
    title: "Domain Services",
    desc: "Secure domain registration, seamless transfers, DNS management, and WHOIS privacy protection.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873433/55BAD0B4-82B7-421C-A712-5219EC2BBEBE_ijs86v.webp",
    icon: FiGlobe,
    list: ["Secure domain registration", "DNS management tools", "WHOIS privacy protection"]
  },
  {
    title: "Business Email",
    desc: "Professional email hosting with Microsoft 365 or Google Workspace, including advanced spam protection.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873492/20AFE026-D86B-4467-A54F-79CE0983839A_hft07q.webp",
    icon: FiMail,
    list: ["Professional @yourdomain", "Advanced spam protection", "Seamless mobile sync"]
  },
  {
    title: "HostFig Studio",
    desc: "High-end catalog imagery, model photography, and product visualization to elevate your brand.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873521/B5E37FFC-62A0-4A45-B488-AABEB6436693_oz9g0w.webp",
    icon: FiCamera,
    list: ["High-end product photography", "Professional catalog imagery", "Brand-aligned visual assets"]
  },
  {
    title: "Branding & Creative",
    desc: "Memorable logo design, comprehensive brand guidelines, and stunning UI/UX design.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873532/E12B1899-4B44-4C48-B5CB-9EA5846BF399_outzv6.webp",
    icon: FiPenTool,
    list: ["Memorable logo design", "Comprehensive brand guidelines", "Stunning UI/UX design"]
  },
  {
    title: "Digital Marketing",
    desc: "Data-driven SEO, Google Ads, content marketing, and conversion rate optimization.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873585/27117BC5-A2B2-444C-A48E-E5BDC7934A55_w1gdff.webp",
    icon: FiTrendingUp,
    list: ["Data-driven SEO strategies", "Targeted Google Ads", "Conversion rate optimization"]
  },
  {
    title: "Website Maintenance",
    desc: "Proactive security updates, performance monitoring, daily backups, and bug fixes.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873608/2F4B5B07-91FB-4E4E-BA95-A190B26814C7_ds0wap.webp",
    icon: FiSettings,
    list: ["Proactive security updates", "Performance monitoring", "Priority bug fixes"]
  },
  {
    title: "Security & QA",
    desc: "Enterprise-grade SSL certificates, web firewalls, DDoS protection, and comprehensive security audits.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873612/B7AFDBE2-2B22-4949-80B0-CE0AC2837F32_wga7gd.webp",
    icon: FiShield,
    list: ["Enterprise web firewalls", "DDoS protection", "Comprehensive security audits"]
  },
  {
    title: "Cloud Infrastructure",
    desc: "Scalable cloud deployments, load balancing, server management, and continuous optimization.",
    img: "https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783873613/FDECCBEE-9611-408A-99D6-84D153441F09_mdjqhf.webp",
    icon: FiCloud,
    list: ["Scalable cloud deployments", "Advanced load balancing", "Continuous optimization"]
  }
];

export default function CoreServices() {
  const [activeTab, setActiveTab] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const activeData = coreServices[activeTab];

  return (
    <section className="w-full bg-[#F6FDFF] py-20 lg:py-32 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      
      <div className="w-full max-w-[1248px] mx-auto relative px-4 md:px-6 flex flex-col items-start gap-12 lg:gap-16">
        {/* Header */}
        <div className="flex flex-col items-start">
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-600/10 bg-blue-600/5 mb-6"
          >
            <SafeIcon icon={FiLayers} className="w-4 h-4 text-blue-600 stroke-[2.5px]" />
            <span className="text-[16px] font-semibold leading-6 tracking-[-0.3px] text-blue-600 uppercase">
              Services
            </span>
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, x: -30 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-[#0F172A] text-[32px] sm:text-[44px] lg:text-[52px] font-bold leading-tight lg:leading-[1.1] tracking-[-1.5px] lg:max-w-[850px]"
          >
            We provide you with the best <span className="text-blue-600">digital solutions</span> your company needs
          </motion.h2>
        </div>

        {/* Tab Row */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="w-full bg-white p-4 lg:px-6 lg:py-4 rounded-2xl shadow-[0_1px_20px_0_rgba(15,23,42,0.04)] flex items-center gap-2 overflow-x-auto overflow-y-hidden scrollbar-hide snap-x"
        >
          {coreServices.map((service, idx) => (
            <button 
              key={idx} 
              onClick={() => setActiveTab(idx)}
              className={`flex items-center gap-3 px-4 sm:px-6 py-2.5 rounded-xl transition-all duration-300 shrink-0 snap-start ${activeTab === idx ? 'bg-blue-600 text-white shadow-md' : 'hover:bg-slate-50 text-slate-500'}`}
            >
              <SafeIcon icon={service.icon} className={`w-[20px] h-[20px] stroke-[2.5px] ${activeTab === idx ? 'text-white' : 'text-slate-400'}`} />
              <span className={`text-sm sm:text-base font-semibold whitespace-nowrap`}>
                {idx + 1 < 10 ? `0${idx + 1}` : idx + 1}. {service.title}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Content Card */}
        <div className="w-full bg-white rounded-[32px] border border-slate-100 shadow-[0_0_40px_0_rgba(15,23,42,0.06)] flex flex-col lg:flex-row items-stretch justify-between overflow-hidden">
          {/* Left Column */}
          <div className="w-full lg:w-1/2 flex flex-col items-start text-left p-8 lg:p-16">
            <AnimatePresence mode="wait">
              <motion.div 
                key={`content-${activeTab}`}
                initial={{ y: 20, opacity: 0 }} 
                animate={{ y: 0, opacity: 1 }} 
                exit={{ y: -20, opacity: 0 }} 
                transition={{ duration: 0.4 }}
                className="w-full"
              >
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center p-4 mb-8 shadow-inner">
                  <SafeIcon icon={activeData.icon} className="w-8 h-8 text-blue-600 stroke-[2px]" />
                </div>
                
                <h3 className="text-[#0F172A] text-[28px] lg:text-[40px] font-bold leading-tight tracking-tight mb-6">
                  {activeData.title}
                </h3>
                
                <p className="text-slate-600 text-lg lg:text-xl font-normal leading-relaxed mb-10 opacity-90">
                  {activeData.desc}
                </p>
                
                <div className="flex flex-col gap-4 mb-12 w-full">
                  {activeData.list.map((item, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-6 h-6 rounded-full bg-blue-600/10 flex items-center justify-center shrink-0">
                        <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-blue-600 stroke-[3px]" />
                      </div>
                      <span className="text-[#0F172A] text-[16px] lg:text-[18px] font-medium tracking-tight">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <motion.button 
              layout 
              onClick={() => navigate('/contact')}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="flex items-center gap-3 rounded-full bg-blue-600 h-16 text-white overflow-hidden shadow-lg shadow-blue-600/20"
              style={{ 
                flexDirection: isHovered ? 'row-reverse' : 'row',
                paddingLeft: isHovered ? '10px' : '28px',
                paddingRight: isHovered ? '28px' : '10px',
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
              }}
            >
              <motion.span layout className="text-lg font-bold leading-[28px] whitespace-nowrap">
                Contact Us
              </motion.span>
              <motion.div layout className="w-11 h-11 rounded-full bg-white flex items-center justify-center shrink-0 z-10 shadow-sm">
                <SafeIcon icon={FiArrowUpRight} className="w-5 h-5 text-blue-600 stroke-[2.5px]" />
              </motion.div>
            </motion.button>
          </div>

          {/* Right Column - Using object-contain to show full image */}
          <div className="w-full lg:w-1/2 min-h-[400px] lg:min-h-[600px] relative overflow-hidden bg-slate-50 flex items-center justify-center p-4">
            <AnimatePresence mode="wait">
              <motion.img 
                key={activeTab}
                initial={{ scale: 0.95, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }} 
                exit={{ scale: 1.05, opacity: 0 }} 
                transition={{ duration: 0.6, ease: "easeOut" }}
                src={activeData.img} 
                alt={activeData.title}
                className="w-full h-full object-contain drop-shadow-2xl"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-100/20 to-transparent pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  );
}